import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Building2,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gift,
  Loader2,
  MapPin,
  Search,
  Shield,
  ShoppingBag,
  Truck,
  UserRound,
  X,
  XCircle,
} from 'lucide-react'
import {
  delegatedExchangesApi,
  exchangesApi,
  hospitalsApi,
  type DelegatedGift,
  type DelegatedGiftContext,
  type DelegatedUser,
} from '../../lib/api'
import { useAuth } from '../../lib/auth'

type DeliveryType = 'selfpickup' | 'express'

interface ExchangeRecordItem {
  id: string
  orderNo: string
  userName: string
  userPhone: string
  giftName: string
  points: number
  status: string
  deliveryType?: string
  hospitalName?: string
  operatorName?: string
  operatorRole?: string
  createdAt?: string
}

interface ExchangeResult {
  exchange: {
    order_no: string
    status: string
  }
  user_points_after: number
  gift_stock_after: number
}

const PAGE_SIZE = 10
const GIFT_PAGE_SIZE = 6

interface GiftQueryOptions {
  page?: number
  keyword?: string
  category?: string
  keepSelection?: boolean
}

const ROLE_LABELS: Record<string, string> = {
  admin: '管理员',
  supervisor: '机构负责人',
  staff: '医护人员',
}

const STATUS_LABELS: Record<string, string> = {
  pending: '待处理',
  confirmed: '待发货',
  shipping: '配送中',
  delivered: '已发货',
  completed: '已完成',
  cancelled: '已取消',
}

function sameLocalDay(value?: string) {
  if (!value) return false
  const target = new Date(value)
  const now = new Date()
  return (
    target.getFullYear() === now.getFullYear() &&
    target.getMonth() === now.getMonth() &&
    target.getDate() === now.getDate()
  )
}

const DelegatedExchange = () => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
  const scopeLabel = isAdmin ? '全平台' : isSupervisor ? '本机构' : '仅本人'

  const [keyword, setKeyword] = useState('')
  const [users, setUsers] = useState<DelegatedUser[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [searching, setSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<DelegatedUser | null>(null)
  const [selectedHospitalId, setSelectedHospitalId] = useState('')
  const [hospitals, setHospitals] = useState<{ id: string; name: string }[]>([])
  const [giftContext, setGiftContext] = useState<DelegatedGiftContext | null>(null)
  const [loadingGifts, setLoadingGifts] = useState(false)
  const [giftSearchInput, setGiftSearchInput] = useState('')
  const [giftKeyword, setGiftKeyword] = useState('')
  const [giftCategory, setGiftCategory] = useState('all')
  const [giftPage, setGiftPage] = useState(1)
  const [selectedGift, setSelectedGift] = useState<DelegatedGift | null>(null)
  const [deliveryType, setDeliveryType] = useState<DeliveryType>('selfpickup')
  const [receiverName, setReceiverName] = useState('')
  const [receiverPhone, setReceiverPhone] = useState('')
  const [receiverAddress, setReceiverAddress] = useState('')
  const [formError, setFormError] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [successResult, setSuccessResult] = useState<ExchangeResult | null>(null)

  const [records, setRecords] = useState<ExchangeRecordItem[]>([])
  const [recordsLoading, setRecordsLoading] = useState(false)
  const [recordsPage, setRecordsPage] = useState(1)
  const [recordsTotal, setRecordsTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState('all')
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0)
  const [cancelRecord, setCancelRecord] = useState<ExchangeRecordItem | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const loadGiftContext = useCallback(
    async (userId: string, hospitalId?: string, options: GiftQueryOptions = {}) => {
      const nextPage = options.page || 1
      const nextKeyword = options.keyword ?? ''
      const nextCategory = options.category ?? ''
      setLoadingGifts(true)
      setFormError('')
      try {
        const context = await delegatedExchangesApi.giftContext(userId, hospitalId, {
          page: nextPage,
          page_size: GIFT_PAGE_SIZE,
          keyword: nextKeyword || undefined,
          category: nextCategory || undefined,
        })
        setGiftContext(context)
        setGiftPage(context.page)
        if (!options.keepSelection) setSelectedGift(null)
      } catch (error) {
        setGiftContext(null)
        setFormError(error instanceof Error ? error.message : '加载礼品失败')
      } finally {
        setLoadingGifts(false)
      }
    },
    [],
  )

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true)
    try {
      const response = await exchangesApi.list({
        page: recordsPage,
        page_size: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        delegated_only: true,
      }) as any
      const items = response?.exchanges || []
      setRecords(
        items.map((item: any) => ({
          id: item.id,
          orderNo: item.order_no || item.id,
          userName: item.user_name || '未知用户',
          userPhone: item.user_phone || '',
          giftName: item.gift_name || '未知礼品',
          points: item.points || 0,
          status: item.status,
          deliveryType: item.delivery_type,
          hospitalName: item.hospital_name,
          operatorName: item.operator_name || '',
          operatorRole: item.operator_role || '',
          createdAt: item.created_at,
        })),
      )
      setRecordsTotal(response?.total || 0)
    } catch (error) {
      console.error('Failed to load delegated exchange records', error)
    } finally {
      setRecordsLoading(false)
    }
  }, [recordsPage, statusFilter])

  useEffect(() => {
    if (!isAdmin) return
    hospitalsApi.list({ page_size: 100 })
      .then((response: any) => {
        const items = (response?.hospitals || response || [])
          .filter((hospital: any) => hospital.status === 'active')
          .map((hospital: any) => ({ id: hospital.id, name: hospital.name }))
        setHospitals(items)
      })
      .catch(console.error)
  }, [isAdmin])

  useEffect(() => {
    loadRecords()
  }, [loadRecords, recordsRefreshKey])

  const handleSearch = async () => {
    const query = keyword.trim()
    if (!query) return
    setSearching(true)
    setHasSearched(true)
    setFormError('')
    try {
      const response = await delegatedExchangesApi.searchUsers(query)
      setUsers(response.items || [])
    } catch (error) {
      setUsers([])
      setFormError(error instanceof Error ? error.message : '搜索失败')
    } finally {
      setSearching(false)
    }
  }

  const resetGiftSelection = () => {
    setGiftContext(null)
    setSelectedGift(null)
    setGiftSearchInput('')
    setGiftKeyword('')
    setGiftCategory('all')
    setGiftPage(1)
    setDeliveryType('selfpickup')
    setReceiverName('')
    setReceiverPhone('')
    setReceiverAddress('')
    setFormError('')
  }

  const handleSelectUser = (target: DelegatedUser) => {
    setSelectedUser(target)
    setSelectedHospitalId(target.hospital_id || '')
    resetGiftSelection()
    if (target.hospital_id) {
      loadGiftContext(target.user_id, target.hospital_id, {
        page: 1,
        keyword: '',
        category: '',
      })
    }
  }

  const handleHospitalChange = (hospitalId: string) => {
    setSelectedHospitalId(hospitalId)
    resetGiftSelection()
    if (selectedUser && hospitalId) {
      loadGiftContext(selectedUser.user_id, hospitalId, {
        page: 1,
        keyword: '',
        category: '',
      })
    }
  }

  const handleGiftSearch = () => {
    if (!selectedUser || !selectedHospitalId) return
    const nextKeyword = giftSearchInput.trim()
    setGiftKeyword(nextKeyword)
    setGiftPage(1)
    setSelectedGift(null)
    loadGiftContext(selectedUser.user_id, selectedHospitalId, {
      page: 1,
      keyword: nextKeyword,
      category: giftCategory === 'all' ? '' : giftCategory,
    })
  }

  const handleGiftCategoryChange = (category: string) => {
    if (!selectedUser || !selectedHospitalId) return
    setGiftCategory(category)
    setGiftPage(1)
    setSelectedGift(null)
    loadGiftContext(selectedUser.user_id, selectedHospitalId, {
      page: 1,
      keyword: giftKeyword,
      category: category === 'all' ? '' : category,
    })
  }

  const handleGiftPageChange = (page: number) => {
    if (!selectedUser || !selectedHospitalId) return
    const totalPages = giftContext?.total_pages || 1
    const nextPage = Math.max(1, Math.min(totalPages, page))
    if (nextPage === giftPage) return
    setGiftPage(nextPage)
    setSelectedGift(null)
    loadGiftContext(selectedUser.user_id, selectedHospitalId, {
      page: nextPage,
      keyword: giftKeyword,
      category: giftCategory === 'all' ? '' : giftCategory,
    })
  }

  const handleSelectGift = (gift: DelegatedGift) => {
    if (!gift.can_exchange) return
    setSelectedGift(gift)
    setFormError('')
    if (gift.delivery_type === 'express') {
      setDeliveryType('express')
    } else if (gift.delivery_type === 'selfpickup') {
      setDeliveryType('selfpickup')
    } else {
      setDeliveryType('selfpickup')
    }
    setReceiverName(giftContext?.user.name || '')
    setReceiverPhone(giftContext?.user.phone || '')
    setReceiverAddress('')
  }

  const validateBeforeConfirm = () => {
    if (!selectedUser || !giftContext || !selectedGift) {
      return '请先选择用户和礼品'
    }
    if (!selectedHospitalId && isAdmin && !giftContext.user.is_bound) {
      return '请选择领取或归属机构'
    }
    if (deliveryType === 'express') {
      if (!receiverName.trim()) return '请填写收件人'
      if (!receiverPhone.trim()) return '请填写联系电话'
      if (!receiverAddress.trim()) return '请填写收货地址'
    }
    return ''
  }

  const openConfirm = () => {
    const error = validateBeforeConfirm()
    if (error) {
      setFormError(error)
      return
    }
    setFormError('')
    setShowConfirm(true)
  }

  const handleSubmit = async () => {
    if (!selectedUser || !selectedGift || !giftContext) return
    setSubmitting(true)
    setFormError('')
    try {
      const result = await delegatedExchangesApi.create({
        user_id: selectedUser.user_id,
        gift_id: selectedGift.id,
        delivery_type: deliveryType,
        hospital_id: selectedHospitalId || undefined,
        receiver_name: deliveryType === 'express' ? receiverName.trim() : undefined,
        phone: deliveryType === 'express' ? receiverPhone.trim() : undefined,
        address: deliveryType === 'express' ? receiverAddress.trim() : undefined,
      }) as ExchangeResult
      setShowConfirm(false)
      setSuccessResult(result)
      setSelectedGift(null)
      await loadGiftContext(selectedUser.user_id, selectedHospitalId, {
        page: giftPage,
        keyword: giftKeyword,
        category: giftCategory === 'all' ? '' : giftCategory,
        keepSelection: true,
      })
      setRecordsPage(1)
      setRecordsRefreshKey((value) => value + 1)
    } catch (error) {
      setShowConfirm(false)
      setFormError(error instanceof Error ? error.message : '代办兑换失败')
    } finally {
      setSubmitting(false)
    }
  }

  const canCancel = (record: ExchangeRecordItem) => {
    if (record.status === 'cancelled' || record.status === 'shipping' || record.status === 'delivered') {
      return false
    }
    if (record.deliveryType === 'express') {
      return record.status === 'confirmed' || record.status === 'pending'
    }
    if (record.deliveryType === 'selfpickup') {
      return record.status === 'completed' && sameLocalDay(record.createdAt)
    }
    return false
  }

  const handleCancel = async () => {
    if (!cancelRecord || !cancelReason.trim()) return
    setCancelling(true)
    try {
      await delegatedExchangesApi.cancel(cancelRecord.id, cancelReason.trim())
      setCancelRecord(null)
      setCancelReason('')
      setRecordsRefreshKey((value) => value + 1)
    } catch (error) {
      alert(error instanceof Error ? error.message : '撤销失败')
    } finally {
      setCancelling(false)
    }
  }

  const pointsAfter = useMemo(() => {
    if (!giftContext || !selectedGift) return null
    return giftContext.user.points - selectedGift.points
  }, [giftContext, selectedGift])

  const totalPages = Math.max(1, Math.ceil(recordsTotal / PAGE_SIZE))
  const giftPageNumbers = useMemo(() => {
    const pageCount = giftContext?.total_pages || 1
    const current = giftContext?.page || 1
    if (pageCount <= 5) {
      return Array.from({ length: pageCount }, (_, index) => index + 1)
    }
    const start = Math.max(1, Math.min(current - 2, pageCount - 4))
    return Array.from({ length: 5 }, (_, index) => start + index)
  }, [giftContext?.page, giftContext?.total_pages])

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-green-600" />
            代为兑换
            <span className="inline-flex items-center gap-1 text-sm font-normal text-green-700 bg-green-50 px-2 py-1 rounded-lg">
              <Shield size={14} /> {scopeLabel}
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin
              ? '可协助全部正常用户兑换，礼品资格仍按用户归属机构校验'
              : isSupervisor
                ? '仅可协助本机构用户兑换'
                : '仅可协助本机构用户，记录范围仅本人'}
          </p>
        </div>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <UserRound className="w-5 h-5 text-green-600" />
          <h2 className="font-semibold text-gray-800">查找用户</h2>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value)
                  setHasSearched(false)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearch()
                }}
                placeholder="姓名、完整手机号/后6位、身份证号/后6位"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !keyword.trim()}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-green-700 text-white font-medium hover:bg-green-800 disabled:bg-gray-200 disabled:text-gray-400"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              搜索
            </button>
          </div>

          {hasSearched && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {users.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">未找到可操作的用户</div>
              ) : users.map((target) => {
                const active = selectedUser?.user_id === target.user_id
                return (
                  <button
                    key={target.user_id}
                    onClick={() => handleSelectUser(target)}
                    className={`w-full p-4 flex items-center gap-3 text-left transition-colors ${active ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-700 text-white flex items-center justify-center font-semibold shrink-0">
                      {target.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-800">{target.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {target.phone || '无手机号'}
                        {target.id_card_masked ? ` · ${target.id_card_masked}` : ''}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {target.hospital_name || '未绑定机构'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-orange-500">{target.points}</p>
                      <p className="text-xs text-gray-400">当前积分</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {selectedUser && (
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 rounded-lg bg-green-50 border border-green-100 p-4">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-green-900">{selectedUser.name}</p>
                <p className="text-sm text-green-700">
                  {selectedUser.phone} · {selectedUser.hospital_name || '未绑定机构'} · {giftContext?.user.points ?? selectedUser.points} 积分
                </p>
              </div>
              {isAdmin && !selectedUser.is_bound && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">领取/归属机构</label>
                  <select
                    value={selectedHospitalId}
                    onChange={(event) => handleHospitalChange(event.target.value)}
                    className="min-w-48 px-3 py-2 border border-green-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">请选择机构</option>
                    {hospitals.map((hospital) => (
                      <option key={hospital.id} value={hospital.id}>{hospital.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={() => {
                  setSelectedUser(null)
                  setKeyword('')
                  setUsers([])
                  setHasSearched(false)
                  resetGiftSelection()
                }}
                className="self-start lg:self-auto p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-white"
                aria-label="清除已选用户"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {formError && (
            <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
              {formError}
            </div>
          )}
        </div>
      </section>

      {selectedUser && (giftContext || loadingGifts) && (
        <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-w-0">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                <h2 className="font-semibold text-gray-800">选择礼品</h2>
                {giftContext && (
                  <span className="text-xs text-gray-400">共 {giftContext.total} 件</span>
                )}
              </div>
              {giftContext && (
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  可用积分 <strong className="text-orange-500">{giftContext.user.points}</strong>
                </span>
              )}
            </div>

            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/70 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={giftSearchInput}
                    onChange={(event) => setGiftSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleGiftSearch()
                    }}
                    placeholder="搜索礼品名称或分类"
                    className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {giftSearchInput && (
                    <button
                      onClick={() => {
                        setGiftSearchInput('')
                        setGiftKeyword('')
                        setGiftPage(1)
                        if (selectedUser && selectedHospitalId) {
                          loadGiftContext(selectedUser.user_id, selectedHospitalId, {
                            page: 1,
                            keyword: '',
                            category: giftCategory === 'all' ? '' : giftCategory,
                          })
                        }
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                      aria-label="清除礼品搜索"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleGiftSearch}
                  className="px-4 py-2.5 rounded-lg bg-green-700 text-white text-sm font-medium hover:bg-green-800"
                >
                  搜索
                </button>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
                <button
                  onClick={() => handleGiftCategoryChange('all')}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${
                    giftCategory === 'all'
                      ? 'bg-green-700 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300'
                  }`}
                >
                  全部
                </button>
                {giftContext?.categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleGiftCategoryChange(category)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${
                      giftCategory === category
                        ? 'bg-green-700 text-white'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 min-h-[340px]">
              {loadingGifts ? (
                <div className="min-h-[300px] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                </div>
              ) : giftContext?.gifts.length ? (
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                  {giftContext.gifts.map((gift) => {
                    const active = selectedGift?.id === gift.id
                    return (
                      <button
                        key={gift.id}
                        onClick={() => handleSelectGift(gift)}
                        disabled={!gift.can_exchange}
                        className={`h-full text-left border-2 rounded-lg overflow-hidden transition-all ${
                          active
                            ? 'border-green-600 ring-2 ring-green-100'
                            : gift.can_exchange
                              ? 'border-gray-200 hover:border-green-300 hover:shadow-sm'
                              : 'border-gray-100 opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="aspect-[5/3] bg-gray-100 relative overflow-hidden">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Gift className="w-9 h-9 text-gray-300" />
                          </div>
                          {gift.image && (
                            <img
                              src={gift.image}
                              alt={gift.name}
                              className="absolute inset-0 w-full h-full object-cover"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none'
                              }}
                            />
                          )}
                          {active && (
                            <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center shadow">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="font-semibold text-gray-800 text-sm leading-5 line-clamp-2 min-h-10">{gift.name}</p>
                          <div className="mt-1 flex items-center justify-between gap-2">
                            <span className="font-bold text-orange-500 text-sm">{gift.points} 积分</span>
                            <span className="text-[11px] text-gray-500">库存 {gift.stock}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400">
                            {gift.delivery_type === 'express' && <span className="inline-flex items-center gap-0.5"><Truck className="w-3 h-3" />快递</span>}
                            {gift.delivery_type === 'selfpickup' && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />自提</span>}
                            {gift.delivery_type === 'both' && <span className="inline-flex items-center gap-0.5"><Truck className="w-3 h-3" />快递/自提</span>}
                            {gift.remaining_limit !== null && gift.remaining_limit !== undefined && (
                              <span>剩余 {gift.remaining_limit} 次</span>
                            )}
                          </div>
                          {gift.disabled_reason && (
                            <p className="mt-1.5 text-[11px] text-red-500 truncate">{gift.disabled_reason}</p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="min-h-[300px] flex flex-col items-center justify-center text-gray-400">
                  <Search className="w-9 h-9 mb-2 opacity-40" />
                  <p className="text-sm">没有匹配的礼品</p>
                </div>
              )}
            </div>

            {giftContext && giftContext.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                  第 {giftContext.page} / {giftContext.total_pages} 页
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleGiftPageChange(giftPage - 1)}
                    disabled={giftPage <= 1}
                    className="p-1.5 rounded border border-gray-200 text-gray-600 disabled:opacity-40"
                    aria-label="上一页礼品"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {giftPageNumbers.map((page) => (
                    <button
                      key={page}
                      onClick={() => handleGiftPageChange(page)}
                      className={`min-w-8 h-8 px-2 rounded text-xs font-medium ${
                        page === giftContext.page
                          ? 'bg-green-700 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handleGiftPageChange(giftPage + 1)}
                    disabled={giftPage >= giftContext.total_pages}
                    className="p-1.5 rounded border border-gray-200 text-gray-600 disabled:opacity-40"
                    aria-label="下一页礼品"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden xl:sticky xl:top-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h2 className="font-semibold text-gray-800">兑换确认</h2>
            </div>
            {selectedGift && giftContext ? (
              <div className="p-5 space-y-4">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">兑换用户</p>
                  <p className="font-semibold text-gray-800 mt-1">{giftContext.user.name}</p>
                  <p className="text-sm text-gray-500">{giftContext.user.phone}</p>
                  <p className="text-sm text-gray-500 mt-1">{giftContext.hospital.name}</p>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">礼品</span>
                  <span className="font-medium text-gray-800">{selectedGift.name}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">本次扣减</span>
                  <span className="font-bold text-orange-500">-{selectedGift.points} 积分</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">兑换后余额</span>
                  <span className="font-semibold text-green-700">{pointsAfter} 积分</span>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">领取方式</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(selectedGift.delivery_type === 'both' || selectedGift.delivery_type === 'selfpickup') && (
                      <button
                        onClick={() => setDeliveryType('selfpickup')}
                        className={`py-2.5 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 ${
                          deliveryType === 'selfpickup'
                            ? 'border-green-600 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600'
                        }`}
                      >
                        <MapPin className="w-4 h-4" /> 现场自提
                      </button>
                    )}
                    {(selectedGift.delivery_type === 'both' || selectedGift.delivery_type === 'express') && (
                      <button
                        onClick={() => setDeliveryType('express')}
                        className={`py-2.5 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 ${
                          deliveryType === 'express'
                            ? 'border-green-600 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600'
                        }`}
                      >
                        <Truck className="w-4 h-4" /> 快递配送
                      </button>
                    )}
                  </div>
                </div>

                {deliveryType === 'express' && (
                  <div className="space-y-3">
                    <input
                      value={receiverName}
                      onChange={(event) => setReceiverName(event.target.value)}
                      placeholder="收件人"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <input
                      value={receiverPhone}
                      onChange={(event) => setReceiverPhone(event.target.value)}
                      placeholder="联系电话"
                      maxLength={11}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <textarea
                      value={receiverAddress}
                      onChange={(event) => setReceiverAddress(event.target.value)}
                      placeholder="详细收货地址"
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                )}

                <button
                  onClick={openConfirm}
                  className="w-full py-3 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800"
                >
                  确认代办并核销
                </button>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-gray-400">
                选择礼品后在此确认兑换信息
              </div>
            )}
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-800">最近代办记录</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isAdmin ? '全平台代办记录' : isSupervisor ? '本机构代办记录' : '本人代办记录'}
            </p>
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value)
              setRecordsPage(1)
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">全部状态</option>
            <option value="completed">已完成</option>
            <option value="confirmed">待发货</option>
            <option value="shipping">配送中</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px]">
            <thead className="bg-gray-50">
              <tr>
                {['订单号', '用户', '礼品', '积分', '方式', '状态', '操作人', '时间', '操作'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recordsLoading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-green-600 mx-auto" />
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-sm text-gray-400">暂无代办记录</td>
                </tr>
              ) : records.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs font-mono text-gray-600">{record.orderNo}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-800">{record.userName}</p>
                    <p className="text-xs text-gray-400">{record.userPhone}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{record.giftName}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-orange-500">{record.points}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {record.deliveryType === 'express' ? '快递' : '自提'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      record.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : record.status === 'cancelled'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-orange-100 text-orange-700'
                    }`}>
                      {STATUS_LABELS[record.status] || record.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {record.operatorName || '-'}
                    <span className="block text-xs text-gray-400">
                      {ROLE_LABELS[record.operatorRole || ''] || record.operatorRole || ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {record.createdAt ? new Date(record.createdAt).toLocaleString('zh-CN') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {canCancel(record) ? (
                      <button
                        onClick={() => {
                          setCancelRecord(record)
                          setCancelReason('')
                        }}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        撤销
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {recordsTotal > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3 text-sm text-gray-500">
            <button
              onClick={() => setRecordsPage((page) => Math.max(1, page - 1))}
              disabled={recordsPage <= 1}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>{recordsPage} / {totalPages}</span>
            <button
              onClick={() => setRecordsPage((page) => Math.min(totalPages, page + 1))}
              disabled={recordsPage >= totalPages}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {showConfirm && selectedUser && selectedGift && giftContext && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">确认代办兑换</h3>
              <button onClick={() => setShowConfirm(false)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex justify-between gap-4"><span className="text-gray-500">用户</span><span className="font-medium text-right">{selectedUser.name} · {selectedUser.phone}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">机构</span><span className="font-medium text-right">{giftContext.hospital.name}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">礼品</span><span className="font-medium text-right">{selectedGift.name}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">领取方式</span><span className="font-medium text-right">{deliveryType === 'express' ? '快递配送' : '现场自提'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">扣减积分</span><span className="font-bold text-orange-500 text-right">-{selectedGift.points}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">兑换后余额</span><span className="font-bold text-green-700 text-right">{pointsAfter}</span></div>
              {deliveryType === 'express' && (
                <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 leading-5">
                  {receiverName} · {receiverPhone}<br />{receiverAddress}
                </div>
              )}
              <div className="rounded-lg bg-orange-50 border border-orange-100 p-3 text-xs text-orange-700">
                确认后将立即扣减用户积分和礼品库存，请核对用户与礼品。
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                返回核对
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-lg bg-green-700 text-white font-medium hover:bg-green-800 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? '核销中...' : '确认核销'}
              </button>
            </div>
          </div>
        </div>
      )}

      {successResult && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-gray-800">代办兑换已完成</h3>
            <p className="mt-1 text-sm text-gray-500 font-mono">{successResult.exchange.order_no}</p>
            <div className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-600 space-y-1.5">
              <p>用户剩余积分：<strong className="text-green-700">{successResult.user_points_after}</strong></p>
              <p>礼品剩余库存：<strong className="text-gray-800">{successResult.gift_stock_after}</strong></p>
            </div>
            <button onClick={() => setSuccessResult(null)} className="mt-5 w-full py-2.5 rounded-lg bg-green-700 text-white font-medium hover:bg-green-800">
              继续办理
            </button>
          </div>
        </div>
      )}

      {cancelRecord && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">撤销代办兑换</h3>
                <p className="text-xs text-gray-400 mt-0.5">{cancelRecord.orderNo}</p>
              </div>
              <button onClick={() => setCancelRecord(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                {cancelRecord.userName} · {cancelRecord.giftName} · {cancelRecord.points} 积分
              </div>
              <textarea
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value.slice(0, 200))}
                rows={3}
                placeholder="请输入撤销原因"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
              <div className="flex items-center gap-2 text-xs text-orange-700 bg-orange-50 rounded-lg p-3">
                <XCircle className="w-4 h-4 shrink-0" />
                撤销后积分和库存会返还，并记录操作日志。
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setCancelRecord(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                取消
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling || !cancelReason.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                确认撤销
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DelegatedExchange
